'use client'
import React from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { EffectCreative, Autoplay } from 'swiper/modules'
import Image from 'next/image'
import Link from 'next/link'
import LabelCard from '../Cards/LabelCard'
import { NewsArticle } from '@/models/news.model'
import { formatDistanceToNow } from 'date-fns'
import 'swiper/css'
import 'swiper/css/effect-creative'
import customViLocale from '@/lib/customViLocale'

interface Props {
  data: NewsArticle[]
  isLoading: boolean
}

const Slider = ({ data, isLoading }: Props) => {
  const sortedNews = [...data].sort((a, b) => {
    const dateA = new Date(a.publishDate).getTime()
    const dateB = new Date(b.publishDate).getTime()
    return dateB - dateA // Newest first
  })
  // Skeleton Loading for each slide
  const renderSkeleton = () => (
    <div className="grid grid-cols-8 gap-4 animate-pulse">
      <div className="relative col-span-9 md:col-span-5">
        <div className="w-full h-[312px] bg-gray-300 rounded" />
      </div>
      <div className="col-span-8 md:col-span-3 flex flex-col gap-2">
        <div className="h-6 bg-gray-300 rounded w-3/4" />
        <div className="h-4 bg-gray-300 rounded w-full" />
        <div className="h-4 bg-gray-300 rounded w-5/6" />
      </div>
    </div>
  )

  return (
    <div className="group">
      {isLoading ? (
        // Skeleton loader
        <Swiper
          modules={[EffectCreative, Autoplay]}
          spaceBetween={30}
          slidesPerView={1}
          loop
          autoplay={{
            delay: 15000,
            disableOnInteraction: false,
          }}
          effect={'creative'}
          creativeEffect={{
            prev: {
              shadow: true,
              translate: [0, 0, -400],
            },
            next: {
              translate: ['100%', 0, 0],
            },
          }}
        >
          {[...Array(3)].map((_, index) => (
            <SwiperSlide key={index}>{renderSkeleton()}</SwiperSlide>
          ))}
        </Swiper>
      ) : (
        // Actual data
        <Swiper
          modules={[EffectCreative, Autoplay]}
          spaceBetween={30}
          slidesPerView={1}
          loop
          autoplay={{
            delay: 15000,
            disableOnInteraction: false,
          }}
          effect={'creative'}
          creativeEffect={{
            prev: {
              shadow: true,
              translate: [0, 0, -400],
            },
            next: {
              translate: ['100%', 0, 0],
            },
          }}
        >
          {sortedNews.map((slide, index) => (
            <SwiperSlide key={index} className="overflow-hidden">
              <article className="grid grid-cols-8 gap-4 bg-white">
                {/* Left: Image and Label */}
                <div className="relative col-span-9 md:col-span-5 h-[360px]">
                  <Link href={`/${slide.slug}`} className="block w-full h-full">
                    <Image
                      src={`${process.env.NEXT_PUBLIC_IMAGE_URL}${slide.imageUrl}`}
                      alt={slide.title || 'title '}
                      fill
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 680px"
                      className="object-cover"
                      priority
                      unoptimized
                    />
                  </Link>
                  <LabelCard categoryId={slide.categoryId} categoryName={slide.categoryName} />
                </div>

                {/* Right: Text Content */}
                <div className="col-span-8 md:col-span-3 flex flex-col justify-between p-2">
                  {/* Category Name and Time */}
                  {/* Title */}
                  <Link href={`/${slide.slug}`} className="block">
                    <div className="text-sm text-gray-500">
                      <span>
                        {formatDistanceToNow(new Date(slide.publishDate), {
                          addSuffix: true,
                          locale: customViLocale,
                        })}
                      </span>
                    </div>
                    <h3 className="text-[20px] font-title font-semibold mb-2 group-hover:text-primary">
                      {slide.title}
                    </h3>
                    {/* Description */}
                    <p className="text-textGray text-justify mb-2 line-clamp-[8]">
                      {slide.description}
                    </p>
                  </Link>
                </div>
              </article>
            </SwiperSlide>
          ))}
        </Swiper>
      )}
    </div>
  )
}

export default Slider
